<template>
    <div class="time-tracker">
        <!-- 添加TopBar组件 -->
        <TopBar :user="user" :apps="apps" />

        <h1>时间记录应用</h1>

        <!-- 计时器区域 -->
        <div class="timer-section">
            <div class="task-input">
                <!-- 添加TodoType下拉列表 -->
                <select v-model="selectedTodoType" class="todo-type-select">
                    <option value="">请选择分类</option>
                    <option v-for="type in todoTypes" :key="type.id" :value="type.id">
                        {{ type.title }}
                    </option>
                </select>
                <input v-model="currentTask" placeholder="输入任务内容（可留空）" list="task-history"
                    @keyup.enter="startTimerForDirectInput" @focus="handleInputFocus" @blur="handleInputBlur"
                    @input="handleInput" />
                <!-- 下拉列表 -->
                <div v-if="showDropdown" class="dropdown-list">
                    <!-- baseTodo列表 -->
                    <div v-if="filteredDropdownItems.baseTodos.length > 0" class="dropdown-group">
                        <div class="dropdown-group-title">基础任务</div>
                        <div v-for="todo in filteredDropdownItems.baseTodos" :key="'base-' + todo.id"
                            class="dropdown-item base-todo-item" @mousedown="selectDropdownItem(todo)">
                            {{ todo.title }}
                        </div>
                    </div>
                    <!-- TodoItem列表 -->
                    <div v-if="filteredDropdownItems.todoItems.length > 0" class="dropdown-group">
                        <div class="dropdown-group-title">任务清单</div>
                        <div v-for="item in filteredDropdownItems.todoItems" :key="'item-' + item.id"
                            class="dropdown-item todo-item" @mousedown="selectDropdownItem(item)">
                            {{ item.title }}
                        </div>
                    </div>
                </div>
                <datalist id="task-history">
                    <option v-for="task in taskHistory" :key="task" :value="task" />
                </datalist>
                <button @click="startTimerForDirectInput" :disabled="isRunning">开始计时</button>
                <button @click="stopTimer" :disabled="!isRunning">结束计时</button>
                <!-- 添加设置按钮 -->
                <button @click="openSettings">设置</button>
            </div>

            <div class="timer-display">
                <div class="time">{{ formatTime(elapsedTime) }}</div>
                <div class="current-task" v-if="currentTaskName">当前任务: {{ currentTaskName || '未命名' }}</div>
            </div>
        </div>

        <!-- 时间记录列表 -->
        <div class="records-section">
            <h2>时间记录</h2>
            <div class="filter-controls">
                <select v-model="timeFilter" @change="filterRecords">
                    <option value="all">全部记录</option>
                    <option value="day">今天</option>
                    <option value="week">本周</option>
                    <option value="month">本月</option>
                    <option value="year">本年</option>
                </select>
            </div>

            <table class="records-table">
                <thead>
                    <tr>
                        <th>任务内容</th>
                        <th>开始时间</th>
                        <th>结束时间</th>
                        <th>持续时间</th>
                        <th>操作</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="record in filteredRecords" :key="record.id"
                        :class="{ 'temp-record': record.isTemporary || record.isTempEndTime }">
                        <td :class="{ 'temp-field': record.isTempEndTime }">{{ record.task || '未命名' }}</td>
                        <td :class="{ 'temp-field': record.isTempEndTime }">{{ formatDateTime(record.startTime) }}</td>
                        <td :class="{ 'temp-field': record.isTempEndTime }">{{ formatDateTime(record.endTime) }}</td>
                        <td :class="{ 'temp-field': record.isTempEndTime }">{{ formatDuration(record.duration) }}</td>
                        <td>
                            <button @click="editRecord(record)">编辑</button>
                            <button @click="deleteRecord(record.id)">删除</button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- 图表统计 -->
        <div class="chart-section" :class="{ 'fullscreen-chart': isChartFullscreen }">
            <h2>时间统计</h2>
            <div class="chart-controls">
                <!-- 添加统计类型切换选项 -->
                <select v-model="chartStatType" @change="handleChartStatTypeChange">
                    <option value="task">按任务统计</option>
                    <option value="category">按分类统计</option>
                    <option value="time">按时间维度统计</option>
                </select>

                <!-- 时间维度选择器（仅在按时间维度统计时显示） -->
                <select v-if="chartStatType === 'time'" v-model="chartTimeDimension" @change="renderChart">
                    <option value="day">按天</option>
                    <option value="week">按周</option>
                    <option value="month">按月</option>
                    <option value="year">按年</option>
                </select>

                <!-- 图表类型选择器 -->
                <select v-model="chartType" @change="renderChart">
                    <option value="bar">柱状图</option>
                    <option value="pie">饼图</option>
                    <option value="line">折线图</option>
                </select>

                <!-- 时间范围筛选器 -->
                <select v-model="chartTimeFilter" @change="renderChart">
                    <option value="all">全部记录</option>
                    <option value="day">今天</option>
                    <option value="week">本周</option>
                    <option value="month">本月</option>
                    <option value="year">本年</option>
                </select>

                <!-- 最大化按钮 -->
                <button @click="toggleChartFullscreen" class="fullscreen-btn">
                    {{ isChartFullscreen ? '退出全屏' : '全屏显示' }}
                </button>
            </div>
            <div ref="chartContainer" class="chart-container"></div>
        </div>

        <!-- 编辑记录弹窗 -->
        <div v-if="showEditModal" class="modal">
            <div class="modal-content">
                <h3>编辑记录</h3>
                <form @submit.prevent="saveEditedRecord">
                    <div>
                        <label>任务内容:</label>
                        <select v-model="editingRecord.task" class="task-select">
                            <option value="">请选择任务</option>
                            <optgroup label="基础任务">
                                <option v-for="todo in baseTodos" :key="'base-' + todo.id" :value="todo.title">
                                    {{ todo.title }}
                                </option>
                            </optgroup>
                            <optgroup label="任务清单">
                                <option v-for="item in todoItems" :key="'item-' + item.id" :value="item.title">
                                    {{ item.title }}
                                </option>
                            </optgroup>
                        </select>
                    </div>
                    <div>
                        <label>开始时间:</label>
                        <input type="datetime-local" v-model="editingRecord.startTime" />
                    </div>
                    <div>
                        <label>结束时间:</label>
                        <input type="datetime-local" v-model="editingRecord.endTime" />
                    </div>
                    <div class="modal-actions">
                        <button type="submit">保存</button>
                        <button @click="closeEditModal">取消</button>
                    </div>
                </form>
            </div>
        </div>

        <!-- 设置弹窗 -->
        <div v-if="showSettingsModal" class="modal">
            <div class="modal-content settings-modal">
                <h3>基础任务管理</h3>
                <div class="settings-content">
                    <!-- 添加新的baseTodo -->
                    <div class="add-base-todo">
                        <h4>添加基础任务</h4>
                        <form @submit.prevent="addBaseTodo">
                            <div class="form-row">
                                <div class="form-group">
                                    <label>任务名称:</label>
                                    <input v-model="newBaseTodo.title" placeholder="请输入任务名称" required />
                                </div>
                                <div class="form-group">
                                    <label>分类:</label>
                                    <select v-model="newBaseTodo.category" required>
                                        <option value="">请选择分类</option>
                                        <option v-for="type in todoTypes" :key="type.id" :value="type.title">
                                            {{ type.title }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            <div class="form-row">
                                <div class="form-group label-full">
                                    <label>备注:</label>
                                    <input v-model="newBaseTodo.category_desc" placeholder="请输入备注信息" />
                                </div>
                            </div>
                            <div class="form-row">
                                <div class="form-group label-full">
                                    <label>关联Todo ID (可选):</label>
                                    <select v-model="newBaseTodo.bind_todo_id">
                                        <option value="">无关联</option>
                                        <option v-for="item in todoItems" :key="item.id" :value="item.id">
                                            {{ item.title }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            <div class="form-actions">
                                <button type="submit">添加基础任务</button>
                            </div>
                        </form>
                    </div>

                    <!-- baseTodo列表 -->
                    <div class="base-todo-list">
                        <h4>现有基础任务</h4>
                        <div v-if="baseTodos.length === 0" class="empty-state">
                            <p>暂无基础任务，请添加新的任务</p>
                        </div>
                        <div class="base-todo-item" v-for="todo in baseTodos" :key="todo.id">
                            <div class="todo-info">
                                <div class="todo-title">{{ todo.title }}</div>
                                <div class="todo-category">分类: {{ todo.typetitle }}</div>
                                <div class="todo-category-desc" v-if="todo.category">备注: {{ todo.category }}</div>
                                <div class="todo-bind-id" v-if="todo.bind_todo_id">关联任务: {{ todo.bind_todo_title }}
                                </div>
                            </div>
                            <div class="todo-actions">
                                <button @click="editBaseTodo(todo)">编辑</button>
                                <button @click="deleteBaseTodo(todo.id)">删除</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-actions">
                    <button @click="closeSettings" class="close-btn">关闭</button>
                </div>
            </div>
        </div>

        <!-- 编辑baseTodo弹窗 -->
        <div v-if="showEditBaseTodoModal" class="modal">
            <div class="modal-content settings-modal">
                <h3>编辑基础任务</h3>
                <div class="settings-content">
                    <form @submit.prevent="saveEditedBaseTodo">
                        <div class="form-row">
                            <div class="form-group">
                                <label>任务名称:</label>
                                <input v-model="editingBaseTodo.title" required />
                            </div>
                            <div class="form-group">
                                <label>分类:</label>
                                <select v-model="editingBaseTodo.category" required>
                                    <option value="">请选择分类</option>
                                    <option v-for="type in todoTypes" :key="type.id" :value="type.title">
                                        {{ type.title }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group label-full">
                                <label>备注:</label>
                                <input v-model="editingBaseTodo.category_desc" placeholder="请输入备注信息" />
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group label-full">
                                <label>关联Todo ID (可选):</label>
                                <select v-model="editingBaseTodo.bind_todo_id">
                                    <option value="">无关联</option>
                                    <option v-for="item in todoItems" :key="item.id" :value="item.id">
                                        {{ item.title }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="form-actions">
                            <button type="submit">保存更改</button>
                            <button @click="closeEditBaseTodoModal" type="button" class="cancel-btn">取消</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts';
import TimeTrackerService from '@/services/timeTrackerService';
import AuthService from '@/services/authService';
import TopBar from '@/components/TopBar.vue'
import UserMainService from '@/services/userMainService.js'
import config from '@/config'
import notificationService from '@/services/notificationService';

export default {
    name: 'TimeTracker',
    components: {
        TopBar
    },
    data() {
        return {
            currentTask: '',
            currentTaskName: '',
            isRunning: false,
            startTime: null,
            elapsedTime: 0,
            timerInterval: null,
            records: [],
            filteredRecords: [],
            timeFilter: 'all',
            chartType: 'bar',
            chartTimeFilter: 'all',
            chartStatType: 'task',  // 新增：统计类型 (task, category, time)
            chartTimeDimension: 'day',  // 新增：时间维度 (day, week, month, year)
            showEditModal: false,
            editingRecord: {
                id: null,
                task: '',
                startTime: '',
                endTime: ''
            },
            taskHistory: [],
            // 新增数据属性
            baseTodos: [],
            todoItems: [],
            showDropdown: false,
            userId: null,
            isDropdownSelection: false,  // 标志：表示是否来自下拉列表的选择
            todoTypes: [],
            selectedTodoType: '',
            // 添加设置相关的数据属性
            showSettingsModal: false,
            showEditBaseTodoModal: false,
            newBaseTodo: {
                title: '',
                category: '',
                category_desc: '',
                bind_todo_id: null
            },
            editingBaseTodo: {
                id: null,
                title: '',
                category: '',
                category_desc: '',
                bind_todo_id: null
            },
            // 全屏显示相关属性
            isChartFullscreen: false,
            // 临时数据管理
            tempRecords: [],
            // 临时记录ID计数器（用于生成临时ID）
            tempRecordIdCounter: 0,
            // 轮询任务ID
            pollingTasks: [],
            // 请求去重和跟踪
            requestQueue: [],
            processingRequests: new Set(),
            // TopBar相关数据
            user: {
                name: '用户名',
                id: 1,
                userType: 1,
                avatar: require('@/assets/logo.png')
            },
            apps: [],
            // 任务添加状态
            isAddingTask: false
        };
    },
    computed: {
        // 计算筛选后的下拉列表项
        filteredDropdownItems() {
            // 第一步：过滤掉被baseTodo绑定的TodoItem
            const boundTodoItemIds = new Set(
                this.baseTodos
                    .filter(todo => todo.bind_todo_id)
                    .map(todo => todo.bind_todo_id)
            );

            const filteredTodoItems = this.todoItems.filter(item =>
                !boundTodoItemIds.has(item.id)
            );

            // 第二步：根据用户输入内容进行模糊匹配
            if (!this.currentTask) {
                // 如果没有输入内容，返回所有未绑定的项目
                return {
                    baseTodos: this.baseTodos,
                    todoItems: filteredTodoItems
                };
            }

            const searchTerm = this.currentTask.toLowerCase();
            const filteredBaseTodos = this.baseTodos.filter(todo =>
                todo.title.toLowerCase().includes(searchTerm)
            );

            const filteredBoundTodoItems = filteredTodoItems.filter(item =>
                item.title.toLowerCase().includes(searchTerm)
            );

            return {
                baseTodos: filteredBaseTodos,
                todoItems: filteredBoundTodoItems
            };
        }
    },
    mounted() {
        // 加载用户信息和应用信息
        this.loadUserInfo();
        this.loadAppInfo();

        // 获取用户ID
        const user = AuthService.getCurrentUser();
        if (user) {
            this.userId = user.userid || user.id;
        }

        this.loadRecords(); // 这会调用loadTodoHistoryList来从服务器获取数据
        this.loadBaseTodos();
        this.loadTodoItems();
        this.loadTodoTypes(); // 加载TodoTypes

        // 获取最新的todoHistory记录并恢复计时
        this.restoreLastTimer();

        // 添加ESC键监听事件
        document.addEventListener('keydown', this.handleEscKey);
    },
    beforeUnmount() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }

        // 清理所有轮询任务
        this.pollingTasks.forEach(task => {
            clearInterval(task);
        });
        this.pollingTasks = [];

        // 移除ESC键监听事件
        document.removeEventListener('keydown', this.handleEscKey);
    },
    methods: {
        // 加载用户信息
        loadUserInfo() {
            const currentUser = AuthService.getCurrentUser();
            if (currentUser) {
                console.log('当前用户信息:', currentUser);
                this.user.name = currentUser.nickname || currentUser.username || '用户名';
                this.user.id = currentUser.userid || currentUser.id || 1;
                this.user.userType = currentUser.userType || 1;
                console.log("icon:", currentUser.icon);
                // 如果有头像信息，则更新头像路径
                if (currentUser.icon) {
                    // 规范化路径分隔符，将反斜杠转换为正斜杠
                    const normalizedIcon = currentUser.icon.replace(/\\/g, '/');
                    console.log('规范化后的icon路径:', normalizedIcon);

                    // 构建完整的后端服务器地址，确保头像从8000端口获取
                    if (normalizedIcon.startsWith('http')) {
                        console.log('完整URL:', normalizedIcon);
                        // 如果已经是完整URL，则直接使用
                        this.user.avatar = normalizedIcon;
                    } else if (normalizedIcon.startsWith('/extra-files/uploads/')) {
                        console.log('extra-files URL:', normalizedIcon);
                        // 从后端服务器的8000端口获取头像
                        this.user.avatar = `${config.API_BASE_URL}${normalizedIcon}`;
                    } else if (normalizedIcon.startsWith('uploads/')) {
                        console.log('uploads URL:', normalizedIcon);
                        // 从后端服务器的8000端口获取头像
                        //this.user.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;

                        this.user.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
                        //查看地址：`${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
                        console.log('user.value.avatar', this.user.avatar);
                    } else {
                        console.log('img URL:', normalizedIcon);
                        this.user.avatar = `${config.API_BASE_URL}/extra-files/uploads/${normalizedIcon}`;
                    }
                } else {
                    console.log('使用默认头像');
                    // 使用默认头像
                    this.user.avatar = require('@/assets/logo.png');
                }
            }
        },

        // 加载应用信息
        async loadAppInfo() {
            try {
                const response = await UserMainService.getAllApps(this.user.userType);
                if (response.status === 200 && response.apps) {
                    this.apps = response.apps.map(app => ({
                        id: app.id,
                        name: app.name,
                        desc: app.desc,
                        link: app.name === '快速抢答' ? '/abhs' : app.link,
                        icon: app.icon || 'code',
                        used: app.used || false
                    }));
                }
            } catch (error) {
                console.error('获取应用数据失败:', error);
                notificationService.error('获取应用数据失败', '错误');
                this.apps = [
                    { id: 1, name: 'OJ练习', desc: '在线编程练习', link: '/CppOJ/', icon: 'code', used: true },
                    { id: 2, name: '任务清单', desc: '待办事项管理', link: '/todo', icon: 'list', used: true },
                    { id: 3, name: '时间线', desc: '时间记录管理', link: '/TimeLine/', icon: 'timeline', used: false },
                    { id: 4, name: '知识图谱', desc: '知识关系展示', link: '/Graph/', icon: 'share', used: false },
                    { id: 5, name: '快速抢答', desc: '抢答与记忆训练', link: '/abhs', icon: 'quiz', used: false },
                    { id: 6, name: '文件管理', desc: '文件上传下载', link: '/FileManage/', icon: 'folder', used: false },
                    { id: 7, name: '数据可视化', desc: '数据图表展示', link: '/DataVisual/', icon: 'bar_chart', used: false }
                ];
            }
        },

        // 恢复最新的计时器状态
        async restoreLastTimer() {
            try {
                // 获取最新的todoHistory记录
                const latestHistoryResponse = await TimeTrackerService.getLatestTodoHistory();

                if (latestHistoryResponse.status === 'ok' && latestHistoryResponse.latest_history) {
                    const latestHistory = latestHistoryResponse.latest_history;

                    // 检查是否有未完成的计时任务（end_time为空）
                    if (!latestHistory.end_time || new Date(latestHistory.end_time).getFullYear() <= 1970) {
                        // 设置当前任务名称
                        this.currentTaskName = latestHistory.base_todo_title;

                        // 设置开始时间
                        this.startTime = new Date(latestHistory.begin_time);

                        // 设置为正在运行状态
                        this.isRunning = true;

                        // 计算已过去的时间
                        this.elapsedTime = new Date() - this.startTime;

                        // 启动计时器
                        this.timerInterval = setInterval(() => {
                            this.elapsedTime = new Date() - this.startTime;
                        }, 1000);

                        console.log('恢复计时器:', {
                            taskName: this.currentTaskName,
                            startTime: this.startTime,
                            elapsedTime: this.elapsedTime
                        });
                    }
                }
            } catch (error) {
                console.error('恢复计时器失败:', error);
                notificationService.error('恢复计时器失败', '错误');
            }
        },

        // 从服务器加载todoHistory列表
        async loadTodoHistoryList(keepTempData = false) {
            try {
                const response = await TimeTrackerService.getTodoHistoryList();
                if (response.status === 'ok') {
                    // 将服务器返回的数据转换为records格式
                    const serverRecords = response.todo_histories.map(history => ({
                        id: history.id,
                        task: history.base_todo_title,
                        startTime: new Date(history.begin_time),
                        endTime: history.end_time ? new Date(history.end_time) : null,
                        duration: history.end_time && new Date(history.end_time).getFullYear() > 1970 ?
                            new Date(history.end_time) - new Date(history.begin_time) :
                            (new Date() - new Date(history.begin_time))
                    }));

                    if (keepTempData) {
                        // 保留临时数据，只更新真实数据
                        const tempRecords = this.records.filter(r => r.isTemporary === true);

                        // 合并服务器数据和临时数据
                        this.records = [...serverRecords, ...tempRecords];
                    } else {
                        this.records = serverRecords;
                    }

                    this.filterRecords();
                    this.renderChart();
                }
            } catch (error) {
                console.error('加载todoHistory列表失败:', error);
                notificationService.error('加载时间记录失败', '错误');
            }
        },

        // 加载指定日期范围的历史数据
        async loadMoreHistory(startDate, endDate) {
            try {
                const response = await TimeTrackerService.getTodoHistoryListByDate(startDate, endDate);
                if (response.status === 'ok') {
                    // 将服务器返回的数据转换为records格式并添加到现有记录中
                    const newRecords = response.todo_histories.map(history => ({
                        id: history.id,
                        task: history.base_todo_title,
                        startTime: new Date(history.begin_time),
                        endTime: history.end_time ? new Date(history.end_time) : null,
                        duration: history.end_time && new Date(history.end_time).getFullYear() > 1970 ?
                            new Date(history.end_time) - new Date(history.begin_time) :
                            (new Date() - new Date(history.begin_time))
                    }));

                    // 合并新记录到现有记录中，避免重复
                    const existingIds = new Set(this.records.map(r => r.id));
                    const uniqueNewRecords = newRecords.filter(r => !existingIds.has(r.id));
                    this.records = [...this.records, ...uniqueNewRecords];

                    this.filterRecords();
                    this.renderChart();
                }
            } catch (error) {
                console.error('加载更多历史数据失败:', error);
                notificationService.error('加载更多历史数据失败', '错误');
            }
        },

        // 按时间维度获取统计数据
        async getStatisticsByTime(startDate, endDate, dimension) {
            try {
                const response = await TimeTrackerService.getStatisticsByTime(startDate, endDate, dimension);
                if (response.status === 'ok') {
                    return response.statistics;
                }
            } catch (error) {
                console.error('获取时间维度统计数据失败:', error);
                notificationService.error('获取时间维度统计数据失败', '错误');
            }
            return [];
        },

        // 按分类获取统计数据
        async getStatisticsByCategory(startDate, endDate) {
            try {
                const response = await TimeTrackerService.getStatisticsByCategory(startDate, endDate);
                console.log('getStatisticsByCategory:', response);
                if (response.status === 'ok') {
                    return response.statistics;
                }
            } catch (error) {
                console.error('获取分类统计数据失败:', error);
                notificationService.error('获取分类统计数据失败', '错误');
            }
            return [];
        },

        // 获取任务每日时间统计数据
        async getTaskDailyStats(startDate, endDate) {
            try {
                const response = await TimeTrackerService.getTaskDailyStats(startDate, endDate);
                if (response.status === 'ok') {
                    return response.statistics;
                }
            } catch (error) {
                console.error('获取任务每日统计数据失败:', error);
                notificationService.error('获取任务每日统计数据失败', '错误');
            }
            return [];
        },

        // 添加临时记录
        addTemporaryRecord() {
            // 检查是否已经有相同的临时记录，避免重复添加
            const existingTempRecord = this.records.find(r =>
                r.isTemporary &&
                r.task === this.currentTaskName &&
                r.startTime.getTime() === this.startTime.getTime()
            );

            if (existingTempRecord) {
                console.log('已存在相同的临时记录，跳过添加');
                return;
            }

            // 获取前一条记录
            const previousRecord = this.records.length > 0 ? this.records[0] : null;

            // 生成临时ID
            const tempId = `temp_${++this.tempRecordIdCounter}`;

            // 创建临时记录
            const tempRecord = {
                id: tempId,
                task: this.currentTaskName,
                startTime: this.startTime,
                endTime: null, // 新增记录的结束时间应该为空
                duration: 0,
                isTemporary: true // 标记为临时数据
            };

            // 将临时记录添加到临时数组
            this.tempRecords.push(tempRecord);

            // 将临时记录添加到显示记录数组的开头
            this.records.unshift(tempRecord);

            // 更新过滤后的记录
            this.filterRecords();
            this.renderChart();

            // 如果前一条记录存在且未结束，临时更新其结束时间
            if (previousRecord && !previousRecord.endTime) {
                // 临时更新前一条记录的结束时间
                previousRecord.endTime = this.startTime;
                previousRecord.duration = this.startTime - previousRecord.startTime;

                // 标记结束时间是临时的
                previousRecord.isTempEndTime = true;

                // 同时更新后端，如果失败则启动轮询
                this.updateRecordOnServer(previousRecord).catch(error => {
                    console.error('更新前一条记录失败，启动轮询:', error);
                    notificationService.error('更新前一条记录失败', '错误');
                    // 如果更新失败，启动轮询机制
                    this.startPollingForRecordUpdate(previousRecord.id);
                });
            }


        },

        // 替换临时记录为真实数据
        replaceTemporaryRecord(realId) {
            // 查找对应的临时记录（临时记录的ID以'temp_'开头）
            const tempRecordIndex = this.records.findIndex(record => record.id.toString().startsWith('temp_'));
            if (tempRecordIndex !== -1) {
                const tempRecord = this.records[tempRecordIndex];

                // 检查是否已经存在相同ID的真实记录，避免重复替换
                const existingRealRecord = this.records.find(record =>
                    record.id === realId &&
                    !record.isTemporary
                );

                if (existingRealRecord) {
                    console.log('已存在相同ID的真实记录，跳过替换');
                    // 移除临时记录
                    this.records.splice(tempRecordIndex, 1);
                    const tempRecordInTempArrayIndex = this.tempRecords.findIndex(r => r.id === tempRecord.id);
                    if (tempRecordInTempArrayIndex !== -1) {
                        this.tempRecords.splice(tempRecordInTempArrayIndex, 1);
                    }
                    this.filterRecords();
                    this.renderChart();
                    return;
                }

                // 从records数组中替换临时记录为真实数据
                this.records[tempRecordIndex].id = realId;
                this.records[tempRecordIndex].isTemporary = false; // 移除临时标记

                // 从tempRecords数组中移除对应的临时记录
                const tempRecordInTempArrayIndex = this.tempRecords.findIndex(r => r.id === tempRecord.id);
                if (tempRecordInTempArrayIndex !== -1) {
                    this.tempRecords.splice(tempRecordInTempArrayIndex, 1);
                }

                // 更新过滤后的记录
                this.filterRecords();
                this.renderChart();
            }
        },

        // 使用真实数据替换指定的临时记录
        replaceTemporaryRecordWithRealData(tempId, realHistory) {
            const tempRecordIndex = this.records.findIndex(record => record.id === tempId && record.isTemporary);
            if (tempRecordIndex !== -1) {
                const tempRecord = this.records[tempRecordIndex];

                // 检查是否已经存在相同ID的真实记录，避免重复替换
                const existingRealRecord = this.records.find(record =>
                    record.id === realHistory.id &&
                    !record.isTemporary
                );

                if (existingRealRecord) {
                    console.log('已存在相同ID的真实记录，跳过替换');
                    // 移除临时记录
                    this.records.splice(tempRecordIndex, 1);
                    const tempRecordInTempArrayIndex = this.tempRecords.findIndex(r => r.id === tempRecord.id);
                    if (tempRecordInTempArrayIndex !== -1) {
                        this.tempRecords.splice(tempRecordInTempArrayIndex, 1);
                    }
                    this.filterRecords();
                    this.renderChart();
                    return;
                }

                // 更新临时记录为真实数据
                this.records[tempRecordIndex].id = realHistory.id;
                this.records[tempRecordIndex].task = realHistory.base_todo_title;
                this.records[tempRecordIndex].startTime = new Date(realHistory.begin_time);
                this.records[tempRecordIndex].endTime = realHistory.end_time ? new Date(realHistory.end_time) : null;
                this.records[tempRecordIndex].duration = realHistory.end_time ?
                    (new Date(realHistory.end_time) - new Date(realHistory.begin_time)) :
                    (new Date() - new Date(realHistory.begin_time));
                this.records[tempRecordIndex].isTemporary = false; // 移除临时标记

                // 从tempRecords数组中移除对应的临时记录
                const tempRecordInTempArrayIndex = this.tempRecords.findIndex(r => r.id === tempId);
                if (tempRecordInTempArrayIndex !== -1) {
                    this.tempRecords.splice(tempRecordInTempArrayIndex, 1);
                }

                // 更新过滤后的记录
                this.filterRecords();
                this.renderChart();
            }
        },



        // 处理服务器恢复后的临时数据重新提交
        async handleServerRecovery() {
            // 防止重复处理服务器恢复
            if (this.processingRequests.has('server_recovery')) {
                console.log('服务器恢复正在处理中，跳过');
                return;
            }

            // 标记服务器恢复正在处理
            this.processingRequests.add('server_recovery');

            try {
                console.log('开始处理服务器恢复，检查临时数据...');

                // 获取所有临时记录
                const tempRecords = this.records.filter(r => r.isTemporary === true);
                console.log('找到临时记录数量:', tempRecords.length);

                // 区分两种临时记录：
                // 1. 没有ID的全新临时记录（如A）- ID以 'temp_' 开头
                // 2. 有ID但结束时间是临时的记录（如B）- 有数字ID但有临时结束时间

                // 首先处理ID以'temp_'开头的全新临时记录（如A）
                const newTempRecords = tempRecords.filter(r => typeof r.id === 'string' && r.id.startsWith('temp_'));
                console.log('找到新临时记录数量:', newTempRecords.length);

                for (const newTempRecord of newTempRecords) {
                    console.log('处理新临时记录:', newTempRecord);
                    try {
                        // 处理全新的临时记录（如A），这会触发后端自动结束前一条任务
                        // 查找或创建baseTodo
                        const existingBaseTodo = this.baseTodos.find(todo => todo.title === newTempRecord.task);

                        let baseTodoResponse;
                        if (existingBaseTodo) {
                            baseTodoResponse = {
                                status: 'ok',
                                base_todo: {
                                    id: existingBaseTodo.id,
                                    title: existingBaseTodo.title,
                                    category: existingBaseTodo.category
                                }
                            };
                            console.log('使用现有baseTodo:', existingBaseTodo);
                        } else {
                            // 创建新的baseTodo
                            console.log('创建新baseTodo:', newTempRecord.task);
                            baseTodoResponse = await TimeTrackerService.createBaseTodo({
                                title: newTempRecord.task,
                                category: newTempRecord.task,
                                bind_todo_id: null,
                                typeid: this.selectedTodoType || null
                            });
                        }

                        if (baseTodoResponse.status === 'ok') {
                            console.log('baseTodo创建成功，开始创建todoHistory');
                            // 创建todoHistory - 这会自动结束前一条未完成的记录
                            console.log('FRONTEND: Creating new todoHistory in recovery with baseTodo ID:', baseTodoResponse.base_todo.id, 'start time:', newTempRecord.startTime.toISOString(), 'and end_time:', null);
                            const todoHistoryResponse = await TimeTrackerService.createTodoHistory({
                                base_todo_id: baseTodoResponse.base_todo.id,
                                begin_time: newTempRecord.startTime.toISOString(),
                                end_time: null
                            });
                            console.log('FRONTEND: Created todoHistory in recovery response:', todoHistoryResponse);

                            if (todoHistoryResponse.status === 'ok') {
                                console.log('todoHistory创建成功:', todoHistoryResponse.todo_history);

                                // 更新已结束的任务记录
                                if (todoHistoryResponse.updated_histories && Array.isArray(todoHistoryResponse.updated_histories)) {
                                    for (const updatedHistory of todoHistoryResponse.updated_histories) {
                                        console.log('更新已结束的任务:', updatedHistory);
                                        this.updateRecordsTable(updatedHistory);
                                    }
                                }

                                // 用服务器返回的真实数据替换临时的新任务记录
                                const recordIndex = this.records.findIndex(r => r.id === newTempRecord.id && r.isTemporary);
                                if (recordIndex !== -1) {
                                    const todoHistory = todoHistoryResponse.todo_history;
                                    this.records[recordIndex].id = todoHistory.id;
                                    this.records[recordIndex].task = todoHistory.base_todo_title;
                                    this.records[recordIndex].startTime = new Date(todoHistory.begin_time);
                                    this.records[recordIndex].endTime = todoHistory.end_time ? new Date(todoHistory.end_time) : null;
                                    this.records[recordIndex].duration = todoHistory.end_time ?
                                        new Date(todoHistory.end_time) - new Date(todoHistory.begin_time) :
                                        (new Date() - new Date(todoHistory.begin_time));
                                    this.records[recordIndex].isTemporary = false;
                                    console.log('临时记录已更新为真实数据:', this.records[recordIndex]);
                                }
                            } else {
                                console.error('todoHistory创建失败:', todoHistoryResponse);
                                notificationService.error(todoHistoryResponse.message || '创建任务记录失败', '错误');
                            }
                        } else {
                            console.error('baseTodo创建失败:', baseTodoResponse);
                            notificationService.error(baseTodoResponse.message || '创建基础任务失败', '错误');
                        }
                    } catch (error) {
                        console.error('处理临时新记录失败:', error);
                        notificationService.error('处理临时新记录失败', '错误');
                    }
                }

                // 重新加载所有数据以获取服务器上自动更新的前一条任务
                console.log('重新加载所有数据...');
                await this.loadTodoHistoryList();

                // 更新界面
                this.filterRecords();
                this.renderChart();

            } catch (error) {
                console.error('处理服务器恢复失败:', error);
                notificationService.error('处理服务器恢复失败', '错误');
            } finally {
                // 清除服务器恢复处理标记
                this.processingRequests.delete('server_recovery');
            }
        },

        // 启动轮询以获取真实数据
        startPollingForRealData(tempId) {
            // 创建轮询任务
            const pollingTask = setInterval(async () => {
                try {
                    // 检查是否还有临时记录需要替换
                    const tempRecord = this.records.find(record => record.id === tempId && record.isTemporary);
                    if (!tempRecord) {
                        // 如果临时记录已被替换，清除轮询任务
                        clearInterval(pollingTask);
                        this.pollingTasks = this.pollingTasks.filter(task => task !== pollingTask);
                        return;
                    }

                    // 检查服务器是否可用，获取最新的todoHistory
                    try {
                        const latestHistoryResponse = await TimeTrackerService.getLatestTodoHistory();
                        if (latestHistoryResponse.status === 'ok' && latestHistoryResponse.latest_history) {
                            const latestHistory = latestHistoryResponse.latest_history;

                            // 检查最新历史记录是否与当前临时记录匹配（基于任务名称和开始时间）
                            const tempTaskMatch = tempRecord.task === latestHistory.base_todo_title &&
                                Math.abs(new Date(latestHistory.begin_time).getTime() - tempRecord.startTime.getTime()) < 60000; // 1分钟内的差异

                            if (tempTaskMatch && latestHistory.id) {
                                // 找到匹配的真实记录，替换临时记录
                                this.replaceTemporaryRecordWithRealData(tempId, latestHistory);

                                // 清除轮询任务
                                clearInterval(pollingTask);
                                this.pollingTasks = this.pollingTasks.filter(task => task !== pollingTask);
                                return;
                            }
                        }
                    } catch (error) {
                        console.log('检查服务器最新记录时出错，继续轮询:', error);
                    }
                } catch (error) {
                    console.error('轮询获取真实数据失败:', error);
                    notificationService.error('轮询获取真实数据失败', '错误');
                }
            }, 5000); // 每5秒轮询一次

            // 将轮询任务添加到数组中以便后续管理
            this.pollingTasks.push(pollingTask);
        },

        // 更新服务器上的记录
        async updateRecordOnServer(record) {
            // 生成请求唯一标识符，防止重复提交
            const requestKey = `update_record_${record.id}_${record.startTime.getTime()}`;

            // 检查是否正在处理相同请求
            if (this.processingRequests.has(requestKey)) {
                console.log('记录更新请求正在处理中，跳过');
                return Promise.resolve();
            }

            // 标记请求正在处理
            this.processingRequests.add(requestKey);

            try {
                // 检查结束时间是否有效
                const endTime = record.endTime && record.endTime.getFullYear() > 1970 ? record.endTime.toISOString() : null;
                console.log('FRONTEND: Updating todoHistory record ID:', record.id, 'with begin_time:', record.startTime.toISOString(), 'end_time:', endTime);
                const response = await TimeTrackerService.updateTodoHistory(record.id, {
                    begin_time: record.startTime.toISOString(),
                    end_time: endTime,
                    task_title: record.task  // 发送任务标题
                });
                console.log('FRONTEND: Update response:', response);

                if (response.status === 'ok') {
                    console.log('记录更新成功 1');
                    // 标记结束时间不再是临时的
                    if (record.isTempEndTime) {
                        record.isTempEndTime = false;
                    }
                    return response; // 返回响应以支持链式调用
                } else {
                    console.error('记录更新失败:', response.message);
                    notificationService.error(response.message, '错误');
                    throw new Error(response.message);
                }
            } catch (error) {
                console.error('更新服务器记录时出错:', error);
                notificationService.error('更新服务器记录时出错', '错误');
                throw error; // 抛出错误以便调用方处理
            } finally {
                // 清除请求处理标记
                this.processingRequests.delete(requestKey);
            }
        },

        // 启动轮询以更新特定记录
        startPollingForRecordUpdate(recordId) {
            // 创建轮询任务
            const pollingTask = setInterval(async () => {
                try {
                    // 检查记录是否仍然存在
                    const record = this.records.find(r => r.id === recordId);
                    if (!record) {
                        // 如果记录不存在，清除轮询任务
                        clearInterval(pollingTask);
                        this.pollingTasks = this.pollingTasks.filter(task => task !== pollingTask);
                        return;
                    }

                    // 尝试更新服务器上的记录
                    try {
                        await this.updateRecordOnServer(record);

                        // 检查是否还有临时记录需要处理（A记录）
                        const hasTempRecords = this.records.some(r => typeof r.id === 'string' && r.id.startsWith('temp_'));
                        if (!hasTempRecords) {
                            // 如果没有临时记录了，检查是否还有临时结束时间
                            const hasTempEndTimes = this.records.some(r => r.isTempEndTime);
                            if (!hasTempEndTimes) {
                                // 如果都没有了，清除轮询任务
                                clearInterval(pollingTask);
                                this.pollingTasks = this.pollingTasks.filter(task => task !== pollingTask);
                                return;
                            }
                        }
                    } catch (error) {
                        console.log('继续轮询，服务器仍未恢复');
                    }
                } catch (error) {
                    console.error('轮询更新记录失败:', error);
                    notificationService.error('轮询更新记录失败', '错误');
                }
            }, 10000); // 增加轮询间隔到5秒，减少重复请求

            // 将轮询任务添加到数组中以便后续管理
            this.pollingTasks.push(pollingTask);
        },



        startTimer() {
            // 重置下拉选择标志，确保状态正确
            this.isDropdownSelection = false;

            // 清除现有的计时器
            if (this.timerInterval) {
                clearInterval(this.timerInterval);
            }

            this.currentTaskName = this.currentTask || '未命名';
            this.startTime = new Date();
            this.isRunning = true;
            this.elapsedTime = 0;

            this.timerInterval = setInterval(() => {
                this.elapsedTime = new Date() - this.startTime;
            }, 1000);

            // 添加到任务历史
            if (this.currentTask && !this.taskHistory.includes(this.currentTask)) {
                this.taskHistory.push(this.currentTask);
                // 不再使用localStorage保存任务历史
            }

            // 清空输入框
            this.currentTask = '';
        },

        // 防重复的开始计时处理函数
        handleStartTimerWithDebounce() {
            // 生成请求唯一标识符，防止重复提交
            const requestKey = `start_timer_${this.currentTaskName}_${this.startTime.getTime()}`;

            // 如果已经在处理中，直接返回
            if (this.processingRequests.has(requestKey)) {
                console.log('开始计时请求正在处理中，跳过重复请求');
                return Promise.resolve(null);
            }

            // 标记请求正在处理
            this.processingRequests.add(requestKey);

            // 设置任务添加状态
            this.isAddingTask = true;

            // 执行实际的开始计时逻辑
            return this.handleStartTimerCore()
                .then(todoHistoryId => {
                    // 如果成功创建了todoHistory，更新记录表
                    if (todoHistoryId) {
                        // 重新加载所有记录以确保数据同步
                        this.loadTodoHistoryList();
                    }
                    return todoHistoryId;
                })
                .finally(() => {
                    // 确保无论成功或失败都清除处理标记
                    this.processingRequests.delete(requestKey);
                    // 重置任务添加状态
                    this.isAddingTask = false;
                });
        },

        async handleStartTimerCore() {
            try {
                console.log('currentTaskName:', this.currentTaskName);
                // 如果用户输入了任务内容，则创建新的baseTodo和todoHistory
                if (this.currentTaskName) {
                    // 首先检查是否已经存在同名的baseTodo
                    const existingBaseTodo = this.baseTodos.find(todo => todo.title === this.currentTaskName);

                    let baseTodoResponse;
                    if (existingBaseTodo) {
                        // 如果已经存在同名的baseTodo，直接使用它
                        baseTodoResponse = {
                            status: 'ok',
                            base_todo: {
                                id: existingBaseTodo.id,
                                title: existingBaseTodo.title,
                                category: existingBaseTodo.category
                            }
                        };
                    } else {
                        // 如果不存在同名的baseTodo，创建一个新的
                        baseTodoResponse = await TimeTrackerService.createBaseTodo({
                            title: this.currentTaskName,
                            category: this.currentTaskName,
                            bind_todo_id: null,
                            // 添加typeid字段
                            typeid: this.selectedTodoType || null
                        });
                    }

                    console.log('baseTodoResponse:', baseTodoResponse);
                    if (baseTodoResponse.status === 'ok') {
                        console.log('FRONTEND: Creating new todoHistory with baseTodo ID:', baseTodoResponse.base_todo.id, 'start time:', this.startTime.toISOString(), 'and end_time:', null);
                        const todoHistoryResponse = await TimeTrackerService.createTodoHistory({
                            base_todo_id: baseTodoResponse.base_todo.id,
                            begin_time: this.startTime.toISOString(),
                            end_time: null
                        });
                        console.log('FRONTEND: Created todoHistory response:', todoHistoryResponse);
                        console.log('todoHistoryResponse:', todoHistoryResponse);

                        if (todoHistoryResponse.status === 'ok') {
                            console.log('创建todoHistory成功:', todoHistoryResponse.todo_history);
                            notificationService.success('任务记录已创建', '成功');
                            // 重新加载baseTodos以包含新创建的项
                            await this.loadBaseTodos();

                            // 更新已结束的任务记录
                            if (todoHistoryResponse.updated_histories && Array.isArray(todoHistoryResponse.updated_histories)) {
                                for (const updatedHistory of todoHistoryResponse.updated_histories) {
                                    console.log('更新已结束的任务:', updatedHistory);
                                    this.updateRecordsTable(updatedHistory);
                                }
                            }

                            // 更新新创建的任务记录
                            this.updateRecordsTable(todoHistoryResponse.todo_history);

                            // 返回todoHistory ID，供stopTimer使用
                            return todoHistoryResponse.todo_history.id;
                        } else {
                            // 如果创建失败，也需要处理临时记录
                            console.error('创建todoHistory失败:', todoHistoryResponse);
                            notificationService.error(todoHistoryResponse.message || '创建任务记录失败', '错误');
                        }
                    }
                }
            } catch (error) {
                console.error('处理开始计时失败:', error);
                notificationService.error('开始计时失败', '错误');
                // 如果服务器断开，启动轮询机制以确保临时数据最终被发送到服务器
                // 临时数据已经添加到界面，现在需要确保它在服务器恢复后被正确处理
                return null; // 返回null表示需要继续使用轮询机制
            }
            return null;
        },

        // 保留原始的handleStartTimer方法用于向后兼容
        async handleStartTimer() {
            return this.handleStartTimerWithDebounce();
        },

        async stopTimer() {
            if (!this.isRunning) return;

            clearInterval(this.timerInterval);
            this.isRunning = false;

            const endTime = new Date();

            // 对于手动输入的任务，我们需要更新最新的todoHistory的结束时间
            if (this.currentTaskName) {
                try {
                    // 检查最新的todoHistory记录的end_time
                    console.log('FRONTEND: In stopTimer - Checking latest todoHistory record');
                    const latestHistoryResponse = await TimeTrackerService.getLatestTodoHistory();
                    console.log('FRONTEND: In stopTimer - Latest todoHistory response:', latestHistoryResponse);
                    if (latestHistoryResponse.status === 'ok' && latestHistoryResponse.latest_history) {
                        const latestHistory = latestHistoryResponse.latest_history;
                        console.log('FRONTEND: In stopTimer - Latest history record found, ID:', latestHistory.id, 'end_time:', latestHistory.end_time);
                        // 只有当该记录的开始时间与当前任务的开始时间相近时，才更新它的结束时间
                        // 这样可以避免错误地结束其他任务
                        const timeDiff = Math.abs(new Date(latestHistory.begin_time).getTime() - this.startTime.getTime());
                        console.log('FRONTEND: Time difference between latest history and current task:', timeDiff);

                        // 如果时间差小于5秒，认为是同一个任务
                        if (timeDiff < 5000) {
                            // 如果end_time为空或为无效时间（如1970年），则更新它
                            if (!latestHistory.end_time || new Date(latestHistory.end_time).getFullYear() <= 1970) {
                                console.log('FRONTEND: In stopTimer - Updating end_time for record ID:', latestHistory.id, 'with time:', endTime.toISOString());
                                await TimeTrackerService.updateTodoHistoryEndTime(latestHistory.id, endTime.toISOString());

                                // 更新records中对应的记录
                                const recordIndex = this.records.findIndex(r => r.id === latestHistory.id);
                                if (recordIndex !== -1) {
                                    this.records[recordIndex].endTime = endTime;
                                    this.records[recordIndex].duration = endTime - this.records[recordIndex].startTime;

                                    this.filterRecords();
                                    this.renderChart();
                                }
                            }
                        } else {
                            console.log('FRONTEND: Skipping end_time update - latest history does not match current task');
                        }
                    }
                } catch (error) {
                    console.error('处理停止计时失败:', error);
                    notificationService.error('停止计时失败', '错误');
                }
            }

            this.filterRecords();
            this.renderChart();

            // 移除自动开始下一个任务的逻辑
            // 现在由用户显式地按回车来开始新任务
            this.currentTaskName = '';
        },

        formatTime(ms) {
            const totalSeconds = Math.floor(ms / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        },

        formatDateTime(date) {
            // 如果日期无效（如1970年），则不显示
            if (!date || new Date(date).getFullYear() <= 1970) {
                return '';
            }
            return new Date(date).toLocaleString('zh-CN');
        },

        formatDuration(ms) {
            const totalSeconds = Math.floor(ms / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            if (hours > 0) {
                return `${hours}小时${minutes}分钟${seconds}秒`;
            } else if (minutes > 0) {
                return `${minutes}分钟${seconds}秒`;
            } else {
                return `${seconds}秒`;
            }
        },

        loadRecords() {
            // 从服务器获取todoHistory列表
            this.loadTodoHistoryList();
        },

        // 更新records-table
        updateRecordsTable(todoHistory) {
            // 检查是否已存在相同ID的记录
            const existingIndex = this.records.findIndex(r => r.id === todoHistory.id);

            // 检查是否是临时记录的ID
            const tempRecordIndex = this.records.findIndex(r => r.id.toString().startsWith('temp_') && r.task === todoHistory.base_todo_title && r.startTime.getTime() === new Date(todoHistory.begin_time).getTime());

            if (existingIndex !== -1) {
                // 更新现有记录
                // 检查结束时间是否有效
                const endTime = todoHistory.end_time && new Date(todoHistory.end_time).getFullYear() > 1970 ? new Date(todoHistory.end_time) : null;
                this.records[existingIndex].endTime = endTime;
                this.records[existingIndex].duration = endTime ?
                    endTime - this.records[existingIndex].startTime :
                    this.records[existingIndex].duration;
                this.records[existingIndex].task = todoHistory.base_todo_title;
                this.records[existingIndex].isTemporary = false; // 确保标记为非临时数据
            } else if (tempRecordIndex !== -1) {
                // 如果存在临时记录，则更新临时记录为真实数据
                const endTime = todoHistory.end_time && new Date(todoHistory.end_time).getFullYear() > 1970 ? new Date(todoHistory.end_time) : null;
                this.records[tempRecordIndex].id = todoHistory.id;
                this.records[tempRecordIndex].task = todoHistory.base_todo_title;
                this.records[tempRecordIndex].startTime = new Date(todoHistory.begin_time);
                this.records[tempRecordIndex].endTime = endTime;
                this.records[tempRecordIndex].duration = endTime ?
                    endTime - new Date(todoHistory.begin_time) :
                    (new Date() - new Date(todoHistory.begin_time));
                this.records[tempRecordIndex].isTemporary = false; // 确保标记为非临时数据
            } else {
                // 创建一个新的记录对象并添加到records数组的开头
                // 检查结束时间是否有效
                const endTime = todoHistory.end_time && new Date(todoHistory.end_time).getFullYear() > 1970 ? new Date(todoHistory.end_time) : null;
                const newRecord = {
                    id: todoHistory.id,
                    task: todoHistory.base_todo_title,
                    startTime: new Date(todoHistory.begin_time),
                    endTime: endTime,
                    duration: endTime ? endTime - new Date(todoHistory.begin_time) : 0,
                    isTemporary: false // 确保标记为非临时数据
                };

                this.records.unshift(newRecord);
            }

            this.filterRecords();
            this.renderChart();
        },

        // 处理输入框获得焦点事件
        handleInputFocus() {
            this.showDropdown = true;
        },

        // 处理输入框失去焦点事件
        handleInputBlur() {
            // 延迟隐藏下拉列表，以便用户可以点击选项
            setTimeout(() => {
                this.showDropdown = false;
            }, 200);
        },

        // 处理输入框输入事件
        handleInput() {
            // 显示下拉列表以显示筛选结果
            this.showDropdown = true;
        },

        // 选择下拉列表项
        async selectDropdownItem(item) {
            // 检查是否正在添加任务
            if (this.isAddingTask) {
                notificationService.warning('任务添加中，请稍候...', '提示');
                return; // 如果正在添加任务，则不执行任何操作
            }

            this.currentTask = item.title;
            this.showDropdown = false;

            // 设置标志，表示这是来自下拉列表的选择
            this.isDropdownSelection = true;

            // 设置任务添加状态
            this.isAddingTask = true;

            // 添加临时数据
            this.currentTaskName = item.title;
            this.startTime = new Date();

            // 根据项目类型处理不同的逻辑
            try {
                if (item.bind_todo_id !== undefined) {
                    // baseTodo项目
                    await this.handleBaseTodoSelection(item);
                } else if (item.typeid !== undefined) {
                    // TodoItem项目
                    await this.handleTodoItemSelection(item);
                }

                // 通知用户任务选择成功
                notificationService.success(`已选择任务: ${item.title}`, '任务选择成功');
            } catch (error) {
                console.error('选择下拉列表项失败:', error);
                notificationService.error('选择任务失败', '错误');
            } finally {
                // 重置标志
                this.isDropdownSelection = false;
                // 重置任务添加状态
                this.isAddingTask = false;
            }

            // 注意：startTimer已经被handleBaseTodoSelection或handleTodoItemSelection处理，不需要再次调用
            // this.startTimer();
        },

        // 处理baseTodo选择
        async handleBaseTodoSelection(item) {
            try {
                // 直接使用选中的baseTodo创建todoHistory
                console.log('FRONTEND: Creating new todoHistory with base_todo_id:', item.id, 'and end_time:', null);
                const todoHistoryResponse = await TimeTrackerService.createTodoHistory({
                    base_todo_id: item.id,
                    begin_time: new Date().toISOString(),
                    end_time: null
                });
                console.log('FRONTEND: Created todoHistory response:', todoHistoryResponse);

                if (todoHistoryResponse.status === 'ok') {
                    console.log('创建todoHistory成功:', todoHistoryResponse.todo_history);

                    // 更新已结束的任务记录
                    if (todoHistoryResponse.updated_histories && Array.isArray(todoHistoryResponse.updated_histories)) {
                        for (const updatedHistory of todoHistoryResponse.updated_histories) {
                            console.log('更新已结束的任务:', updatedHistory);
                            this.updateRecordsTable(updatedHistory);
                        }
                    }

                    // 更新新创建的任务记录
                    this.updateRecordsTable(todoHistoryResponse.todo_history);

                    // 通知用户baseTodo选择成功
                    notificationService.success(`已开始计时: ${item.title}`, '计时开始');
                } else {
                    notificationService.error('创建任务记录失败', '错误');
                }
            } catch (error) {
                console.error('处理baseTodo选择失败:', error);
                notificationService.error('处理baseTodo选择失败', '错误');
            }
        },

        // 处理TodoItem选择
        async handleTodoItemSelection(item) {
            try {
                // 根据TodoItem创建新的baseTodo
                const baseTodoResponse = await TimeTrackerService.createBaseTodo({
                    title: item.title,
                    category: item.category,
                    bind_todo_id: item.id
                });

                if (baseTodoResponse.status === 'ok') {
                    // 使用新创建的baseTodo创建todoHistory
                    console.log('FRONTEND: Creating new todoHistory with baseTodo ID:', baseTodoResponse.base_todo.id, 'and end_time:', null);
                    const todoHistoryResponse = await TimeTrackerService.createTodoHistory({
                        base_todo_id: baseTodoResponse.base_todo.id,
                        begin_time: new Date().toISOString(),
                        end_time: null
                    });
                    console.log('FRONTEND: Created todoHistory response:', todoHistoryResponse);

                    if (todoHistoryResponse.status === 'ok') {
                        console.log('创建todoHistory成功:', todoHistoryResponse.todo_history);
                        // 重新加载baseTodos以包含新创建的项
                        await this.loadBaseTodos();

                        // 更新已结束的任务记录
                        if (todoHistoryResponse.updated_histories && Array.isArray(todoHistoryResponse.updated_histories)) {
                            for (const updatedHistory of todoHistoryResponse.updated_histories) {
                                console.log('更新已结束的任务:', updatedHistory);
                                this.updateRecordsTable(updatedHistory);
                            }
                        }

                        // 更新新创建的任务记录
                        this.updateRecordsTable(todoHistoryResponse.todo_history);

                        // 通知用户TodoItem选择成功
                        notificationService.success(`已开始计时: ${item.title}`, '计时开始');
                    } else {
                        notificationService.error('创建任务记录失败', '错误');
                    }
                } else {
                    notificationService.error('创建基础任务失败', '错误');
                }
            } catch (error) {
                console.error('处理TodoItem选择失败:', error);
                notificationService.error('处理TodoItem选择失败', '错误');
            }
        },

        // 加载baseTodos
        async loadBaseTodos() {
            try {
                const response = await TimeTrackerService.getBaseTodos();
                if (response.status === 'ok') {
                    this.baseTodos = response.base_todos;
                }
            } catch (error) {
                console.error('加载baseTodos失败:', error);
                notificationService.error('加载基础任务失败', '错误');
            }
        },

        // 加载todoItems
        async loadTodoItems() {
            try {
                const response = await TimeTrackerService.getTodoItems();
                if (response.status === 'ok') {
                    this.todoItems = response.todo_items;
                }
            } catch (error) {
                console.error('加载todoItems失败:', error);
                notificationService.error('加载任务清单失败', '错误');
            }
        },

        // 加载TodoTypes
        async loadTodoTypes() {
            try {
                const response = await TimeTrackerService.getTodoTypes();
                if (response.status === 'ok') {
                    this.todoTypes = response.todo_types;
                }
            } catch (error) {
                console.error('加载TodoTypes失败:', error);
                notificationService.error('加载任务分类失败', '错误');
            }
        },

        // 用户直接输入任务时开始计时
        startTimerForDirectInput() {
            // 检查是否正在添加任务
            if (this.isAddingTask) {
                notificationService.warning('任务添加中，请稍候...', '提示');
                return; // 如果正在添加任务，则不执行任何操作
            }

            // 重置下拉选择标志，确保状态正确
            this.isDropdownSelection = false;

            // 如果已经在运行，先停止当前计时器
            if (this.isRunning) {
                console.log('计时器已经在运行，先停止当前计时器');
                this.stopTimer();
            }

            // 清除现有的计时器
            if (this.timerInterval) {
                clearInterval(this.timerInterval);
            }

            this.currentTaskName = this.currentTask || '未命名';
            this.startTime = new Date();
            this.isRunning = true;
            this.elapsedTime = 0;

            this.timerInterval = setInterval(() => {
                this.elapsedTime = new Date() - this.startTime;
            }, 1000);

            // 添加到任务历史
            if (this.currentTask && !this.taskHistory.includes(this.currentTask)) {
                this.taskHistory.push(this.currentTask);
                // 不再使用localStorage保存任务历史
            }

            // 清空输入框
            this.currentTask = '';

            // 处理直接输入的任务创建
            this.handleStartTimerWithDebounce();
        },


        filterRecords() {
            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startOfWeek = new Date(startOfDay);
            startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const startOfYear = new Date(now.getFullYear(), 0, 1);

            switch (this.timeFilter) {
                case 'day':
                    this.filteredRecords = this.records.filter(record =>
                        new Date(record.startTime) >= startOfDay
                    );
                    break;
                case 'week':
                    this.filteredRecords = this.records.filter(record =>
                        new Date(record.startTime) >= startOfWeek
                    );
                    break;
                case 'month':
                    this.filteredRecords = this.records.filter(record =>
                        new Date(record.startTime) >= startOfMonth
                    );
                    break;
                case 'year':
                    this.filteredRecords = this.records.filter(record =>
                        new Date(record.startTime) >= startOfYear
                    );
                    break;
                default:
                    this.filteredRecords = [...this.records];
            }
        },

        editRecord(record) {
            // 修复时区问题：使用本地时间格式化而不是toISOString
            const formatLocalDateTime = (date) => {
                // 如果日期无效（如1970年），则不显示
                if (!date || new Date(date).getFullYear() <= 1970) {
                    return '';
                }
                const d = new Date(date);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const hours = String(d.getHours()).padStart(2, '0');
                const minutes = String(d.getMinutes()).padStart(2, '0');
                return `${year}-${month}-${day}T${hours}:${minutes}`;
            };

            this.editingRecord = {
                id: record.id,
                task: record.task || '',
                startTime: formatLocalDateTime(record.startTime),
                endTime: formatLocalDateTime(record.endTime)
            };
            this.showEditModal = true;
        },

        // 获取任务对应的bind_todo_id
        getBindTodoIdForTask(taskTitle) {
            // 首先在baseTodos中查找
            const baseTodo = this.baseTodos.find(todo => todo.title === taskTitle);
            if (baseTodo && baseTodo.bind_todo_id) {
                return baseTodo.bind_todo_id;
            }

            // 然后在todoItems中查找
            const todoItem = this.todoItems.find(item => item.title === taskTitle);
            if (todoItem) {
                return `item-${todoItem.id}`;
            }

            return null;
        },

        // 获取bind_todo_id对应的任务标题
        getTaskTitleForBindTodoId(bindTodoId) {
            if (!bindTodoId) return '';

            // 检查是否为baseTodo的bind_todo_id
            if (!bindTodoId.startsWith('item-')) {
                const baseTodo = this.baseTodos.find(todo => todo.bind_todo_id == bindTodoId);
                if (baseTodo) {
                    return baseTodo.title;
                }
            } else {
                // 提取itemId
                const itemId = bindTodoId.replace('item-', '');
                const todoItem = this.todoItems.find(item => item.id == itemId);
                if (todoItem) {
                    return todoItem.title;
                }
            }

            return '';
        },

        saveEditedRecord() {
            const index = this.records.findIndex(r => r.id === this.editingRecord.id);
            if (index !== -1) {
                // 更新记录
                this.records[index].task = this.editingRecord.task;
                this.records[index].startTime = new Date(this.editingRecord.startTime);
                // 检查结束时间是否有效
                const endTime = this.editingRecord.endTime && new Date(this.editingRecord.endTime).getFullYear() > 1970 ?
                    new Date(this.editingRecord.endTime) : null;
                this.records[index].endTime = endTime;
                this.records[index].duration = endTime ? endTime - this.records[index].startTime : 0;

                // 获取任务对应的bind_todo_id
                const bindTodoId = this.getBindTodoIdForTask(this.editingRecord.task);
                if (bindTodoId) {
                    // 如果需要，可以在这里添加更新bind_todo_id的逻辑
                    // 目前我们只是更新任务标题
                }

                // 调用后端API保存修改
                this.saveRecordToServer(this.records[index]);

                this.filterRecords();
                this.renderChart();
                this.closeEditModal();
            }
        },

        // 保存记录到服务器
        async saveRecordToServer(record) {
            try {
                // 检查结束时间是否有效
                const endTime = record.endTime && record.endTime.getFullYear() > 1970 ? record.endTime.toISOString() : null;
                const response = await TimeTrackerService.updateTodoHistory(record.id, {
                    begin_time: record.startTime.toISOString(),
                    end_time: endTime,
                    task_title: record.task  // 发送任务标题
                });

                if (response.status === 'ok') {
                    console.log('记录更新成功 2');
                } else {
                    console.error('记录更新失败:', response.message);
                    notificationService.error(response.message, '错误');
                }
            } catch (error) {
                console.error('保存记录到服务器时出错:', error);
                notificationService.error('保存记录到服务器时出错', '错误');
            }
        },

        closeEditModal() {
            this.showEditModal = false;
        },

        deleteRecord(id) {
            if (confirm('确定要删除这条记录吗？')) {
                this.records = this.records.filter(record => record.id !== id);

                this.filterRecords();
                this.renderChart();
            }
        },

        renderChart() {
            // 根据统计类型选择不同的渲染方式
            if (this.chartStatType === 'category') {
                this.renderCategoryChart();
            } else if (this.chartStatType === 'time') {
                this.renderTimeDimensionChart();
            } else {
                this.renderTaskChart();
            }
        },

        // 处理统计类型切换
        handleChartStatTypeChange() {
            // 当统计类型改变时，重新渲染图表
            this.renderChart();
        },

        // 按任务统计图表
        async renderTaskChart() {
            // 过滤图表数据
            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startOfWeek = new Date(startOfDay);
            startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const startOfYear = new Date(now.getFullYear(), 0, 1);

            let chartData = [...this.records];

            switch (this.chartTimeFilter) {
                case 'day':
                    chartData = chartData.filter(record =>
                        new Date(record.startTime) >= startOfDay
                    );
                    break;
                case 'week':
                    chartData = chartData.filter(record =>
                        new Date(record.startTime) >= startOfWeek
                    );
                    break;
                case 'month':
                    chartData = chartData.filter(record =>
                        new Date(record.startTime) >= startOfMonth
                    );
                    break;
                case 'year':
                    chartData = chartData.filter(record =>
                        new Date(record.startTime) >= startOfYear
                    );
                    break;
            }

            // 按任务分组统计时间
            const taskStats = {};
            chartData.forEach(record => {
                const task = record.task || '未命名';
                if (!taskStats[task]) {
                    taskStats[task] = 0;
                }
                taskStats[task] += record.duration;
            });

            // 准备图表数据
            const taskNames = Object.keys(taskStats);
            const durations = Object.values(taskStats).map(duration => {
                // 确保duration是数值类型并转换为小时
                const durationInHours = parseFloat(duration) / (1000 * 60 * 60);
                return Math.round(durationInHours * 100) / 100;
            });

            // 调试日志
            console.log('Task chart data:', { taskNames, durations, taskStats });

            // 初始化图表
            if (!this.chart) {
                this.chart = echarts.init(this.$refs.chartContainer);
            }

            // 根据图表类型渲染
            if (this.chartType === 'bar') {
                const option = {
                    title: {
                        text: '任务时间统计（小时）'
                    },
                    tooltip: {},
                    xAxis: {
                        type: 'category',
                        data: taskNames
                    },
                    yAxis: {
                        type: 'value'
                    },
                    series: [{
                        type: 'bar',
                        data: durations,
                        itemStyle: {
                            color: '#409EFF'
                        }
                    }]
                };
                this.chart.setOption(option, true);
            } else if (this.chartType === 'line') {
                // 折线图：分析每项任务在不同天数里的累计时间变化
                this.renderTaskLineChart();
            } else {
                // 饼图
                const pieData = taskNames.map((name, index) => ({
                    name: name,
                    value: durations[index]
                }));

                const option = {
                    title: {
                        text: '任务时间占比',
                        left: 'center'
                    },
                    tooltip: {
                        trigger: 'item',
                        formatter: '{a} <br/>{b}: {c}小时 ({d}%)'
                    },
                    legend: {
                        orient: 'vertical',
                        left: 'left'
                    },
                    series: [
                        {
                            name: '时间占比',
                            type: 'pie',
                            radius: '50%',
                            data: pieData,
                            emphasis: {
                                itemStyle: {
                                    shadowBlur: 10,
                                    shadowOffsetX: 0,
                                    shadowColor: 'rgba(0, 0, 0, 0.5)'
                                }
                            }
                        }
                    ]
                };
                this.chart.setOption(option, true);
            }
        },

        // 折线图：分析每项任务在不同天数里的累计时间变化
        renderTaskLineChart() {
            // 获取当前筛选的时间范围
            const now = new Date();
            let startDate, endDate;

            switch (this.chartTimeFilter) {
                case 'day': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
                    break;
                }
                case 'week': {
                    const startOfWeek = new Date(now);
                    startOfWeek.setDate(now.getDate() - now.getDay());
                    startDate = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate());
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 7);
                    endDate = new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate());
                    break;
                }
                case 'month': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                    endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                    break;
                }
                case 'year': {
                    startDate = new Date(now.getFullYear(), 0, 1);
                    endDate = new Date(now.getFullYear() + 1, 0, 1);
                    break;
                }
                default: {
                    // 默认获取最近30天的数据
                    endDate = new Date();
                    startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
                }
            }

            // 调用后端API获取任务每日统计数据
            // 如果是"all"选项，则传递特殊标识
            const startDateParam = this.chartTimeFilter === 'all' ? 'all' : startDate.toISOString().split('.')[0];
            const endDateParam = this.chartTimeFilter === 'all' ? 'all' : endDate.toISOString().split('.')[0];

            this.getTaskDailyStats(
                startDateParam,
                endDateParam
            ).then(statistics => {
                // 准备折线图数据
                // 获取所有日期
                const allDates = new Set();
                statistics.forEach(taskStat => {
                    taskStat.daily_stats.forEach(stat => {
                        allDates.add(stat.date);
                    });
                });
                const sortedDates = Array.from(allDates).sort();

                // 为每个任务创建数据系列
                const series = statistics.map(taskStat => {
                    const dataMap = {};
                    taskStat.daily_stats.forEach(stat => {
                        dataMap[stat.date] = stat.duration;
                    });

                    const data = sortedDates.map(date => {
                        return dataMap[date] || 0;
                    });

                    return {
                        name: taskStat.task,
                        type: 'line',
                        data: data,
                        smooth: true
                    };
                });

                // 设置图表选项
                const option = {
                    title: {
                        text: '任务时间变化趋势'
                    },
                    tooltip: {
                        trigger: 'axis',
                        formatter: (params) => {
                            // 检查参数有效性
                            if (!params || params.length === 0) {
                                return '';
                            }

                            // 查找有数据的系列（鼠标悬停时通常只有一个系列有数据）
                            for (let i = 0; i < params.length; i++) {
                                if (params[i].data !== undefined && params[i].data !== null) {
                                    // 如果数据不为0，则显示该条线的数据
                                    if (params[i].data !== 0) {
                                        return `${params[i].name}<br/>${params[i].seriesName}: ${params[i].data}小时`;
                                    }
                                    // 如果数据为0，不显示
                                    break;
                                }
                            }

                            // 如果没有特定高亮的系列，显示所有非零数据
                            let result = '';
                            for (let i = 0; i < params.length; i++) {
                                if (params[i].data !== undefined && params[i].data !== null && params[i].data !== 0) {
                                    result += `${params[i].seriesName}: ${params[i].data}小时<br/>`;
                                }
                            }
                            return result ? params[0].name + '<br/>' + result : '';
                        }
                    },
                    legend: {
                        data: statistics.map(stat => stat.task),
                        bottom: 10
                    },
                    xAxis: {
                        type: 'category',
                        data: sortedDates
                    },
                    yAxis: {
                        type: 'value',
                        name: '时间（小时）'
                    },
                    series: series
                };

                this.chart.setOption(option, true);
            });
        },

        // // 按分类统计图表
        // async renderCategoryChart() {
        //     // 过滤图表数据
        //     const now = new Date();
        //     const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        //     const startOfWeek = new Date(startOfDay);
        //     startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        //     const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        //     const startOfYear = new Date(now.getFullYear(), 0, 1);

        //     let chartData = [...this.records];

        //     switch (this.chartTimeFilter) {
        //         case 'day':
        //             chartData = chartData.filter(record =>
        //                 new Date(record.startTime) >= startOfDay
        //             );
        //             break;
        //         case 'week':
        //             chartData = chartData.filter(record =>
        //                 new Date(record.startTime) >= startOfWeek
        //             );
        //             break;
        //         case 'month':
        //             chartData = chartData.filter(record =>
        //                 new Date(record.startTime) >= startOfMonth
        //             );
        //             break;
        //         case 'year':
        //             chartData = chartData.filter(record =>
        //                 new Date(record.startTime) >= startOfYear
        //             );
        //             break;
        //     }

        //     // 按分类分组统计时间
        //     const categoryStats = {};
        //     chartData.forEach(record => {
        //         const category = record.category || '未分类';
        //         if (!categoryStats[category]) {
        //             categoryStats[category] = 0;
        //         }
        //         categoryStats[category] += record.duration;
        //     });

        //     // 准备图表数据
        //     const categoryNames = Object.keys(categoryStats);
        //     const durations = Object.values(categoryStats).map(duration => {
        //         // 确保duration是数值类型并转换为小时
        //         const durationInHours = parseFloat(duration) / (1000 * 60 * 60);
        //         return Math.round(durationInHours * 100) / 100;
        //     });

        //     // 调试日志
        //     console.log('Category chart data:', { categoryNames, durations, categoryStats });

        //     // 初始化图表
        //     if (!this.chart) {
        //         this.chart = echarts.init(this.$refs.chartContainer);
        //     }

        //     // 根据图表类型渲染
        //     if (this.chartType === 'bar') {
        //         const option = {
        //             title: {
        //                 text: '分类时间统计（小时）'
        //             },
        //             tooltip: {},
        //             xAxis: {
        //                 type: 'category',
        //                 data: categoryNames
        //             },
        //             yAxis: {
        //                 type: 'value'
        //             },
        //             series: [{
        //                 type: 'bar',
        //                 data: durations,
        //                 itemStyle: {
        //                     color: '#409EFF'
        //                 }
        //             }]
        //         };
        //         this.chart.setOption(option, true);
        //     } else if (this.chartType === 'line') {
        //         // 折线图：分析每项分类在不同天数里的累计时间变化
        //         this.renderCategoryLineChart();
        //     } else {
        //         // 饼图
        //         const pieData = categoryNames.map((name, index) => ({
        //             name: name,
        //             value: durations[index]
        //         }));

        //         const option = {
        //             title: {
        //                 text: '分类时间占比',
        //                 left: 'center'
        //             },
        //             tooltip: {
        //                 trigger: 'item',
        //                 formatter: '{a} <br/>{b}: {c}小时 ({d}%)'
        //             },
        //             legend: {
        //                 orient: 'vertical',
        //                 left: 'left'
        //             },
        //             series: [
        //                 {
        //                     name: '时间占比',
        //                     type: 'pie',
        //                     radius: '50%',
        //                     data: pieData,
        //                     emphasis: {
        //                         itemStyle: {
        //                             shadowBlur: 10,
        //                             shadowOffsetX: 0,
        //                             shadowColor: 'rgba(0, 0, 0, 0.5)'
        //                         }
        //                     }
        //                 }
        //             ]
        //         };
        //         this.chart.setOption(option, true);
        //     }
        // },

        // 折线图：分析每项分类在不同天数里的累计时间变化
        renderCategoryLineChart() {
            // 获取当前筛选的时间范围
            const now = new Date();
            let startDate, endDate;

            switch (this.chartTimeFilter) {
                case 'day': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
                    break;
                }
                case 'week': {
                    const startOfWeek = new Date(now);
                    startOfWeek.setDate(now.getDate() - now.getDay());
                    startDate = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate());
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 7);
                    endDate = new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate());
                    break;
                }
                case 'month': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                    endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                    break;
                }
                case 'year': {
                    startDate = new Date(now.getFullYear(), 0, 1);
                    endDate = new Date(now.getFullYear() + 1, 0, 1);
                    break;
                }
                default: {
                    // 默认获取最近30天的数据
                    endDate = new Date();
                    startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
                }
            }

            // 调用后端API获取分类每日统计数据
            this.getStatisticsByTime(
                startDate.toISOString().split('.')[0],
                endDate.toISOString().split('.')[0],
                this.chartTimeDimension
            ).then(statistics => {
                // 准备折线图数据
                // 获取所有日期
                const allDates = new Set();
                statistics.forEach(categoryStat => {
                    categoryStat.daily_stats.forEach(stat => {
                        allDates.add(stat.date);
                    });
                });
                const sortedDates = Array.from(allDates).sort();

                // 为每个分类创建数据系列
                const series = statistics.map(categoryStat => {
                    const dataMap = {};
                    categoryStat.daily_stats.forEach(stat => {
                        dataMap[stat.date] = stat.duration;
                    });

                    const data = sortedDates.map(date => {
                        return dataMap[date] || 0;
                    });

                    return {
                        name: categoryStat.category,
                        type: 'line',
                        data: data,
                        smooth: true
                    };
                });

                // 设置图表选项
                const option = {
                    title: {
                        text: '分类时间变化趋势'
                    },
                    tooltip: {
                        trigger: 'axis'
                    },
                    legend: {
                        data: statistics.map(stat => stat.category),
                        bottom: 10
                    },
                    xAxis: {
                        type: 'category',
                        data: sortedDates
                    },
                    yAxis: {
                        type: 'value',
                        name: '时间（小时）'
                    },
                    series: series
                };

                this.chart.setOption(option, true);
            });
        },

        // // 按分类统计图表
        // async renderCategoryChart() {
        //     // 过滤图表数据
        //     const now = new Date();
        //     const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        //     const startOfWeek = new Date(startOfDay);
        //     startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        // },

        // 按时间维度统计图表
        // async renderTimeDimensionChart() {
        //     // 过滤图表数据
        //     const now = new Date();
        //     const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        //     const startOfWeek = new Date(startOfDay);
        //     startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        //     const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        //     const startOfYear = new Date(now.getFullYear(), 0, 1);

        //     let chartData = [...this.records];

        //     switch (this.chartTimeFilter) {
        //         case 'day':
        //             chartData = chartData.filter(record =>
        //                 new Date(record.startTime) >= startOfDay
        //             );
        //             break;
        //         case 'week':
        //             chartData = chartData.filter(record =>
        //                 new Date(record.startTime) >= startOfWeek
        //             );
        //             break;
        //         case 'month':
        //             chartData = chartData.filter(record =>
        //                 new Date(record.startTime) >= startOfMonth
        //             );
        //             break;
        //         case 'year':
        //             chartData = chartData.filter(record =>
        //                 new Date(record.startTime) >= startOfYear
        //             );
        //             break;
        //     }

        //     // 按时间维度分组统计时间
        //     const timeStats = {};
        //     chartData.forEach(record => {
        //         const timeDimension = this.getTimeDimension(record.startTime);
        //         if (!timeStats[timeDimension]) {
        //             timeStats[timeDimension] = 0;
        //         }
        //         timeStats[timeDimension] += record.duration;
        //     });

        //     // 准备图表数据
        //     const timeDimensions = Object.keys(timeStats);
        //     const durations = Object.values(timeStats).map(duration => {
        //         // 确保duration是数值类型并转换为小时
        //         const durationInHours = parseFloat(duration) / (1000 * 60 * 60);
        //         return Math.round(durationInHours * 100) / 100;
        //     });

        //     // 调试日志
        //     console.log('Time dimension chart data:', { timeDimensions, durations, timeStats });

        //     // 初始化图表
        //     if (!this.chart) {
        //         this.chart = echarts.init(this.$refs.chartContainer);
        //     }

        //     // 根据图表类型渲染
        //     if (this.chartType === 'bar') {
        //         const option = {
        //             title: {
        //                 text: '时间维度统计（小时）'
        //             },
        //             tooltip: {},
        //             xAxis: {
        //                 type: 'category',
        //                 data: timeDimensions
        //             },
        //             yAxis: {
        //                 type: 'value'
        //             },
        //             series: [{
        //                 type: 'bar',
        //                 data: durations,
        //                 itemStyle: {
        //                     color: '#409EFF'
        //                 }
        //             }]
        //         };
        //         this.chart.setOption(option, true);
        //     } else if (this.chartType === 'line') {
        //         // 折线图：分析每项时间维度在不同天数里的累计时间变化
        //         this.renderTimeDimensionLineChart();
        //     } else {
        //         // 饼图
        //         const pieData = timeDimensions.map((name, index) => ({
        //             name: name,
        //             value: durations[index]
        //         }));

        //         const option = {
        //             title: {
        //                 text: '时间维度占比',
        //                 left: 'center'
        //             },
        //             tooltip: {
        //                 trigger: 'item',
        //                 formatter: '{a} <br/>{b}: {c}小时 ({d}%)'
        //             },
        //             legend: {
        //                 orient: 'vertical',
        //                 left: 'left'
        //             },
        //             series: [
        //                 {
        //                     name: '时间占比',
        //                     type: 'pie',
        //                     radius: '50%',
        //                     data: pieData,
        //                     emphasis: {
        //                         itemStyle: {
        //                             shadowBlur: 10,
        //                             shadowOffsetX: 0,
        //                             shadowColor: 'rgba(0, 0, 0, 0.5)'
        //                         }
        //                     }
        //                 }
        //             ]
        //         };
        //         this.chart.setOption(option, true);
        //     }
        // },

        // 折线图：分析每项时间维度在不同天数里的累计时间变化
        renderTimeDimensionLineChart() {
            // 获取当前筛选的时间范围
            const now = new Date();
            let startDate, endDate;

            switch (this.chartTimeFilter) {
                case 'day': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
                    break;
                }
                case 'week': {
                    const startOfWeek = new Date(now);
                    startOfWeek.setDate(now.getDate() - now.getDay());
                    startDate = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate());
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 7);
                    endDate = new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate());
                    break;
                }
                case 'month': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                    endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                    break;
                }
                case 'year': {
                    startDate = new Date(now.getFullYear(), 0, 1);
                    endDate = new Date(now.getFullYear() + 1, 0, 1);
                    break;
                }
                default: {
                    // 默认获取最近30天的数据
                    endDate = new Date();
                    startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
                }
            }

            // 调用后端API获取时间维度每日统计数据
            this.getStatisticsByTime(
                startDate.toISOString().split('.')[0],
                endDate.toISOString().split('.')[0],
                this.chartTimeDimension
            ).then(statistics => {
                // 准备折线图数据
                // 获取所有日期
                const allDates = new Set();
                statistics.forEach(timeStat => {
                    timeStat.daily_stats.forEach(stat => {
                        allDates.add(stat.date);
                    });
                });
                const sortedDates = Array.from(allDates).sort();

                // 为每个时间维度创建数据系列
                const series = statistics.map(timeStat => {
                    const dataMap = {};
                    timeStat.daily_stats.forEach(stat => {
                        dataMap[stat.date] = stat.duration;
                    });

                    const data = sortedDates.map(date => {
                        return dataMap[date] || 0;
                    });

                    return {
                        name: timeStat.time_dimension,
                        type: 'line',
                        data: data,
                        smooth: true
                    };
                });

                // 设置图表选项
                const option = {
                    title: {
                        text: '时间维度变化趋势'
                    },
                    tooltip: {
                        trigger: 'axis'
                    },
                    legend: {
                        data: statistics.map(stat => stat.time_dimension),
                        bottom: 10
                    },
                    xAxis: {
                        type: 'category',
                        data: sortedDates
                    },
                    yAxis: {
                        type: 'value',
                        name: '时间（小时）'
                    },
                    series: series
                };

                this.chart.setOption(option, true);
            });
        },

        // 按分类统计图表
        async renderCategoryChart() {
            // 获取当前筛选的时间范围
            const now = new Date();
            let startDate, endDate;

            switch (this.chartTimeFilter) {
                case 'day': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
                    break;
                }
                case 'week': {
                    const startOfWeek = new Date(now);
                    startOfWeek.setDate(now.getDate() - now.getDay());
                    startDate = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate());
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 7);
                    endDate = new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate());
                    break;
                }
                case 'month': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                    endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                    break;
                }
                case 'year': {
                    startDate = new Date(now.getFullYear(), 0, 1);
                    endDate = new Date(now.getFullYear() + 1, 0, 1);
                    break;
                }
                default: {
                    // 默认获取最近30天的数据
                    endDate = new Date();
                    startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
                }
            }

            // 调用后端API获取分类统计数据
            try {
                // 如果是"all"选项，则传递特殊标识
                const startDateParam = this.chartTimeFilter === 'all' ? 'all' : startDate.toISOString().split('.')[0];
                const endDateParam = this.chartTimeFilter === 'all' ? 'all' : endDate.toISOString().split('.')[0];

                const statistics = await this.getStatisticsByCategory(
                    startDateParam,
                    endDateParam
                );

                // 准备图表数据
                const categoryNames = statistics.map(stat => stat.category || '未分类');
                const durations = statistics.map(stat => {
                    // 后端返回的duration已经是小时数，不需要再除以(1000 * 60 * 60)
                    const durationInHours = parseFloat(stat.duration);
                    // 直接保留两位小数
                    return Math.round(durationInHours * 100) / 100;
                });

                // 调试日志
                console.log('Category chart data:', { categoryNames, durations, statistics });

                // 初始化图表
                if (!this.chart) {
                    this.chart = echarts.init(this.$refs.chartContainer);
                }

                // 根据图表类型渲染
                if (this.chartType === 'bar') {
                    const option = {
                        title: {
                            text: '分类时间统计（小时）'
                        },
                        tooltip: {},
                        xAxis: {
                            type: 'category',
                            data: categoryNames
                        },
                        yAxis: {
                            type: 'value'
                        },
                        series: [{
                            type: 'bar',
                            data: durations,
                            itemStyle: {
                                color: '#409EFF'
                            }
                        }]
                    };
                    this.chart.setOption(option, true);
                } else if (this.chartType === 'line') {
                    // 分类折线图
                    const option = {
                        title: {
                            text: '分类时间变化趋势'
                        },
                        tooltip: {
                            trigger: 'axis'
                        },
                        legend: {
                            data: ['时间（小时）'],
                            bottom: 10
                        },
                        xAxis: {
                            type: 'category',
                            data: categoryNames
                        },
                        yAxis: {
                            type: 'value',
                            name: '时间（小时）'
                        },
                        series: [{
                            name: '时间（小时）',
                            type: 'line',
                            data: durations,
                            smooth: true,
                            itemStyle: {
                                color: '#409EFF'
                            }
                        }]
                    };
                    this.chart.setOption(option, true);
                } else {
                    // 饼图
                    const pieData = categoryNames.map((name, index) => ({
                        name: name,
                        value: durations[index]
                    }));

                    const option = {
                        title: {
                            text: '分类时间占比',
                            left: 'center'
                        },
                        tooltip: {
                            trigger: 'item',
                            formatter: '{a} <br/>{b}: {c}小时 ({d}%)'
                        },
                        legend: {
                            orient: 'vertical',
                            left: 'left'
                        },
                        series: [
                            {
                                name: '时间占比',
                                type: 'pie',
                                radius: '50%',
                                data: pieData,
                                emphasis: {
                                    itemStyle: {
                                        shadowBlur: 10,
                                        shadowOffsetX: 0,
                                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                                    }
                                }
                            }
                        ]
                    };
                    this.chart.setOption(option, true);
                }
            } catch (error) {
                console.error('获取分类统计数据失败:', error);
                notificationService.error('获取分类统计数据失败', '错误');
            }
        },

        // 按时间维度统计图表
        async renderTimeDimensionChart() {
            // 获取当前筛选的时间范围
            const now = new Date();
            let startDate, endDate;

            switch (this.chartTimeFilter) {
                case 'day': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
                    break;
                }
                case 'week': {
                    const startOfWeek = new Date(now);
                    startOfWeek.setDate(now.getDate() - now.getDay());
                    startDate = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate());
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 7);
                    endDate = new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate());
                    break;
                }
                case 'month': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                    endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                    break;
                }
                case 'year': {
                    startDate = new Date(now.getFullYear(), 0, 1);
                    endDate = new Date(now.getFullYear() + 1, 0, 1);
                    break;
                }
                default: {
                    // 默认获取最近30天的数据
                    endDate = new Date();
                    startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
                }
            }

            // 调用后端API获取时间维度统计数据
            try {
                // 如果是"all"选项，则传递特殊标识
                const startDateParam = this.chartTimeFilter === 'all' ? 'all' : startDate.toISOString().split('.')[0];
                const endDateParam = this.chartTimeFilter === 'all' ? 'all' : endDate.toISOString().split('.')[0];

                const statistics = await this.getStatisticsByTime(
                    startDateParam,
                    endDateParam,
                    this.chartTimeDimension
                );

                // 准备图表数据
                const periods = statistics.map(stat => stat.period);
                const durations = statistics.map(stat => {
                    // 后端返回的duration已经是小时数，不需要再除以(1000 * 60 * 60)
                    const durationInHours = parseFloat(stat.duration);
                    // 直接保留两位小数
                    return Math.round(durationInHours * 100) / 100;
                });

                // 调试日志
                console.log('Time dimension chart data:', { periods, durations, statistics });

                // 初始化图表
                if (!this.chart) {
                    this.chart = echarts.init(this.$refs.chartContainer);
                }

                // 根据图表类型渲染
                if (this.chartType === 'bar') {
                    const option = {
                        title: {
                            text: `按${this.getTimeDimensionLabel(this.chartTimeDimension)}时间统计（小时）`
                        },
                        tooltip: {},
                        xAxis: {
                            type: 'category',
                            data: periods
                        },
                        yAxis: {
                            type: 'value'
                        },
                        series: [{
                            type: 'bar',
                            data: durations,
                            itemStyle: {
                                color: '#409EFF'
                            }
                        }]
                    };
                    this.chart.setOption(option, true);
                } else if (this.chartType === 'line') {
                    // 时间维度折线图
                    const option = {
                        title: {
                            text: `按${this.getTimeDimensionLabel(this.chartTimeDimension)}时间变化趋势`
                        },
                        tooltip: {
                            trigger: 'axis'
                        },
                        legend: {
                            data: ['时间（小时）'],
                            bottom: 10
                        },
                        xAxis: {
                            type: 'category',
                            data: periods
                        },
                        yAxis: {
                            type: 'value',
                            name: '时间（小时）'
                        },
                        series: [{
                            name: '时间（小时）',
                            type: 'line',
                            data: durations,
                            smooth: true,
                            itemStyle: {
                                color: '#409EFF'
                            }
                        }]
                    };
                    this.chart.setOption(option, true);
                } else {
                    // 饼图
                    const pieData = periods.map((period, index) => ({
                        name: period,
                        value: durations[index]
                    }));

                    const option = {
                        title: {
                            text: `按${this.getTimeDimensionLabel(this.chartTimeDimension)}时间占比`,
                            left: 'center'
                        },
                        tooltip: {
                            trigger: 'item',
                            formatter: '{a} <br/>{b}: {c}小时 ({d}%)'
                        },
                        legend: {
                            orient: 'vertical',
                            left: 'left'
                        },
                        series: [
                            {
                                name: '时间占比',
                                type: 'pie',
                                radius: '50%',
                                data: pieData,
                                emphasis: {
                                    itemStyle: {
                                        shadowBlur: 10,
                                        shadowOffsetX: 0,
                                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                                    }
                                }
                            }
                        ]
                    };
                    this.chart.setOption(option, true);
                }
            } catch (error) {
                console.error('获取时间维度统计数据失败:', error);
                notificationService.error('获取时间维度统计数据失败', '错误');
            }
        },

        // 获取时间维度的中文标签
        getTimeDimensionLabel(dimension) {
            const labels = {
                'day': '天',
                'week': '周',
                'month': '月',
                'year': '年'
            };
            return labels[dimension] || dimension;
        },

        // 添加设置相关的方法
        openSettings() {
            this.showSettingsModal = true;
        },

        closeSettings() {
            this.showSettingsModal = false;
        },

        // 添加新的baseTodo
        async addBaseTodo() {
            // 验证必填字段
            if (!this.newBaseTodo.title) {
                notificationService.warning('请输入任务名称', '警告');
                return;
            }

            if (!this.newBaseTodo.category) {
                notificationService.warning('请选择分类', '警告');
                return;
            }

            // 根据选择的分类标题找到对应的typeid
            let typeid = null;
            if (this.newBaseTodo.category) {
                const matchingType = this.todoTypes.find(type => type.title === this.newBaseTodo.category);
                if (matchingType) {
                    typeid = matchingType.id;
                }
            }

            try {
                const response = await TimeTrackerService.createBaseTodo({
                    title: this.newBaseTodo.title,
                    category: this.newBaseTodo.category_desc,  // 使用备注字段
                    typeid: typeid,  // 添加typeid字段
                    bind_todo_id: this.newBaseTodo.bind_todo_id || null
                });

                if (response.status === 'ok') {
                    // 重新加载baseTodos列表
                    await this.loadBaseTodos();
                    // 重置表单
                    this.newBaseTodo = {
                        title: '',
                        category: '',
                        category_desc: '',
                        bind_todo_id: null
                    };
                    notificationService.success('基础任务添加成功', '成功');
                } else {
                    notificationService.error('添加失败: ' + response.message, '错误');
                }
            } catch (error) {
                console.error('添加baseTodo失败:', error);
                notificationService.error('添加失败，请查看控制台了解详情', '错误');
            }
        },

        // 编辑baseTodo
        editBaseTodo(todo) {
            // 使用typeid来匹配分类，如果不存在则使用category作为备用
            let categoryValue = '';
            if (todo.typeid) {
                // 根据typeid查找对应的TodoType
                const matchingType = this.todoTypes.find(type => type.id === todo.typeid);
                if (matchingType) {
                    categoryValue = matchingType.title;
                }
            } else if (todo.category) {
                // 如果没有typeid，尝试通过category匹配TodoType
                const matchingType = this.todoTypes.find(type => type.title === todo.category);
                if (matchingType) {
                    categoryValue = matchingType.title;
                } else {
                    // 如果没有找到匹配的TodoType，使用原来的category值
                    categoryValue = todo.category;
                }
            }

            this.editingBaseTodo = {
                ...todo,
                bind_todo_id: todo.bind_todo_id || '',  // 确保bind_todo_id为字符串类型以匹配select选项
                category: categoryValue,  // 确保category正确设置
                category_desc: todo.category  // 备注信息
            };
            this.showEditBaseTodoModal = true;
        }, closeEditBaseTodoModal() {
            this.showEditBaseTodoModal = false;
        },

        // 保存编辑的baseTodo
        async saveEditedBaseTodo() {
            // 验证必填字段
            if (!this.editingBaseTodo.title) {
                notificationService.warning('请输入任务名称', '警告');
                return;
            }

            if (!this.editingBaseTodo.category) {
                notificationService.warning('请选择分类', '警告');
                return;
            }

            // 根据选择的分类标题找到对应的typeid
            let typeid = null;
            if (this.editingBaseTodo.category) {
                const matchingType = this.todoTypes.find(type => type.title === this.editingBaseTodo.category);
                if (matchingType) {
                    typeid = matchingType.id;
                }
            }

            try {
                const response = await TimeTrackerService.updateBaseTodo(this.editingBaseTodo.id, {
                    title: this.editingBaseTodo.title,
                    category: this.editingBaseTodo.category_desc,  // 使用备注字段
                    typeid: typeid,  // 添加typeid字段
                    bind_todo_id: this.editingBaseTodo.bind_todo_id || null
                });

                if (response.status === 'ok') {
                    // 重新加载baseTodos列表
                    await this.loadBaseTodos();
                    this.closeEditBaseTodoModal();
                    notificationService.success('基础任务更新成功', '成功');
                } else {
                    notificationService.error('更新失败: ' + response.message, '错误');
                }
            } catch (error) {
                console.error('更新baseTodo失败:', error);
                notificationService.error('更新失败，请查看控制台了解详情', '错误');
            }
        },

        // 删除baseTodo
        async deleteBaseTodo(id) {
            if (!confirm('确定要删除这个基础任务吗？')) {
                return;
            }

            try {
                const response = await TimeTrackerService.deleteBaseTodo(id);

                if (response.status === 'ok') {
                    // 重新加载baseTodos列表
                    await this.loadBaseTodos();
                    notificationService.success('基础任务删除成功', '成功');
                } else {
                    notificationService.error('删除失败: ' + response.message, '错误');
                }
            } catch (error) {
                console.error('删除baseTodo失败:', error);
                notificationService.error('删除失败，请查看控制台了解详情', '错误');
            }
        },

        // 全屏切换功能
        toggleChartFullscreen() {
            this.isChartFullscreen = !this.isChartFullscreen;

            // 重新渲染图表以适应新的尺寸
            this.$nextTick(() => {
                if (this.chart) {
                    this.chart.resize();
                }

                // 同步更新背景色
                if (typeof this.updateBackgroundColor === 'function') {
                    this.updateBackgroundColor();
                }
            });
        },

        // ESC键退出全屏
        handleEscKey(event) {
            if (event.key === 'Escape' && this.isChartFullscreen) {
                this.isChartFullscreen = false;

                // 重新渲染图表以适应新的尺寸
                this.$nextTick(() => {
                    if (this.chart) {
                        this.chart.resize();
                    }

                    // 同步更新背景色
                    if (typeof this.updateBackgroundColor === 'function') {
                        this.updateBackgroundColor();
                    }
                });
            }
        },

        // 更新背景色方法（用于全屏状态下的背景色同步）
        updateBackgroundColor() {
            // 默认实现：在全屏状态下确保背景色正确
            if (this.isChartFullscreen) {
                // 全屏状态下设置背景色
                document.body.style.backgroundColor = '#f5f5f5';
            } else {
                // 非全屏状态下恢复背景色
                document.body.style.backgroundColor = '';
            }
        }
    }
};
</script>

<style scoped>
.time-tracker {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
}

.timer-section {
    background: #f5f5f5;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
}

.task-input {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    align-items: center;
    position: relative;
}

.task-input input {
    flex: 1;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

/* 添加TodoType下拉列表样式 */
.todo-type-select {
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: white;
    min-width: 120px;
}

.dropdown-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    max-height: 300px;
    overflow-y: auto;
    margin-top: 5px;
}

.dropdown-group {
    padding: 5px 0;
}

.dropdown-group-title {
    padding: 5px 10px;
    font-weight: bold;
    color: #666;
    background-color: #f5f5f5;
}

.dropdown-item {
    padding: 8px 10px;
    cursor: pointer;
}

.dropdown-item:hover {
    background-color: #e6f7ff;
}

.base-todo-item {
    color: blue;
}

.todo-item {
    color: orange;
}

.task-input button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    background: #409EFF;
    color: white;
    cursor: pointer;
}

.task-input button:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.timer-display {
    text-align: center;
}

.time {
    font-size: 48px;
    font-weight: bold;
    color: #333;
    font-family: 'Courier New', monospace;
}

.current-task {
    font-size: 18px;
    color: #666;
    margin-top: 10px;
}

.records-section {
    background: #f5f5f5;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
}

.filter-controls {
    margin-bottom: 15px;
}

.filter-controls select {
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.records-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
}

.records-table th,
.records-table td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

.records-table th {
    background: #409EFF;
    color: white;
}

.records-table button {
    padding: 5px 10px;
    margin-right: 5px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.records-table button:first-child {
    background: #409EFF;
    color: white;
}

.records-table button:last-child {
    background: #F56C6C;
    color: white;
}

/* 临时记录样式 */
.temp-record {
    background-color: #fffacd !important;
    /* 淡黄色背景 */
}

.temp-record td {
    color: #d4af37;
    /* 金色文字 */
    font-weight: bold;
}

/* 临时字段样式 */
.temp-field {
    color: #d4af37 !important;
    /* 金色文字 */
    font-weight: bold;
    background-color: #fffacd !important;
    /* 淡黄色背景 */
}

.chart-section {
    background: #f5f5f5;
    padding: 20px;
    border-radius: 8px;
}

.chart-section.fullscreen-chart {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: #f5f5f5;
    padding: 20px;
    border-radius: 0;
    z-index: 9999;
    box-sizing: border-box;
}

.chart-section.fullscreen-chart .chart-container {
    height: calc(100vh - 100px);
}

.chart-controls {
    margin-bottom: 15px;
    display: flex;
    gap: 10px;
}

.chart-controls select {
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.fullscreen-btn {
    padding: 8px 12px;
    background: #409EFF;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    margin-left: auto;
}

.fullscreen-btn:hover {
    background: #66b1ff;
}

.chart-container {
    width: 100%;
    height: 400px;
    background: white;
    border-radius: 4px;
}

.modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 400px;
}

.modal-content h3 {
    margin-top: 0;
}

.modal-content form div {
    margin-bottom: 15px;
}

.modal-content label {
    display: block;
    margin-bottom: 5px;
}

.modal-content input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
}

.task-select {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
    background-color: white;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.modal-actions button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.modal-actions button:first-child {
    background: #409EFF;
    color: white;
}

.modal-actions button:last-child {
    background: #909399;
    color: white;
}

/* 设置弹窗样式 */
.settings-modal {
    width: 700px;
    max-width: 95vw;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.settings-content {
    max-height: 70vh;
    overflow-y: auto;
    padding: 20px;
}

.add-base-todo {
    margin-bottom: 30px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.add-base-todo h4 {
    margin-top: 0;
    margin-bottom: 20px;
    color: #333;
    font-size: 18px;
    border-bottom: 2px solid #409EFF;
    padding-bottom: 10px;
}

.form-row {
    display: flex;
    gap: 15px;
    margin-bottom: 15px;
}

.form-group {
    flex: 1;
    margin-bottom: 15px;
}

.form-group.label-full {
    flex: 0 0 100%;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #555;
    font-size: 14px;
}

.form-group input,
.form-group select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    box-sizing: border-box;
    font-size: 14px;
    transition: border-color 0.3s;
}

.form-group input:focus,
.form-group select:focus {
    outline: none;
    border-color: #409EFF;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.form-actions {
    text-align: right;
    padding-top: 10px;
}

.form-actions button {
    background: #409EFF;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.3s;
}

.form-actions button:hover {
    background: #66b1ff;
}

.base-todo-list h4 {
    margin-top: 0;
    margin-bottom: 20px;
    color: #333;
    font-size: 18px;
    border-bottom: 2px solid #409EFF;
    padding-bottom: 10px;
}

.base-todo-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    margin-bottom: 12px;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
    transition: box-shadow 0.3s, border-color 0.3s;
}

.base-todo-item:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
    border-color: #dcdfe6;
}

.todo-info {
    flex: 1;
}

.todo-title {
    font-weight: 600;
    margin-bottom: 8px;
    color: #303133;
}

.todo-category {
    color: #606266;
    font-size: 14px;
    margin-bottom: 5px;
}

.todo-category-desc {
    color: #909399;
    font-size: 13px;
    font-style: italic;
    margin-bottom: 5px;
}

.todo-bind-id {
    color: #909399;
    font-size: 13px;
    font-style: italic;
}

.todo-actions {
    display: flex;
    gap: 8px;
}

.todo-actions button {
    padding: 6px 12px;
    font-size: 13px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.3s;
}

.todo-actions button:first-child {
    background: #67c23a;
    color: white;
}

.todo-actions button:first-child:hover {
    background: #85ce61;
}

.todo-actions button:last-child {
    background: #f56c6c;
    color: white;
}

.todo-actions button:last-child:hover {
    background: #f78989;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .settings-modal {
        width: 95%;
    }

    .form-row {
        flex-direction: column;
        gap: 0;
    }

    .base-todo-item {
        flex-direction: column;
        align-items: flex-start;
    }

    .todo-actions {
        align-self: flex-end;
        margin-top: 10px;
    }
}

.modal-content.settings-modal h3 {
    margin-top: 0;
    padding: 20px 20px 0;
    color: #303133;
    font-size: 20px;
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 20px;
}

.empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #909399;
}

.empty-state p {
    margin: 0;
    font-size: 16px;
}

.close-btn {
    background: #909399;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.3s;
}

.close-btn:hover {
    background: #a6a9ad;
}

.cancel-btn {
    background: #909399;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    margin-left: 10px;
    transition: background 0.3s;
}

.cancel-btn:hover {
    background: #a6a9ad;
}

.form-actions .cancel-btn {
    margin-left: 10px;
}
</style>
